'use client'

import { useEffect } from 'react'

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY || ''
const SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`

interface Grecaptcha {
  ready: (cb: () => void) => void
  execute: (siteKey: string, opts: { action: string }) => Promise<string>
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha
  }
}

// Module-level singleton so multiple hook callers share one script load.
let scriptPromise: Promise<void> | null = null

const loadScript = (): Promise<void> => {
  if (typeof window === 'undefined' || !SITE_KEY) return Promise.resolve()
  if (window.grecaptcha) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="https://www.google.com/recaptcha/api.js"]`,
    )
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA load failed')), {
        once: true,
      })
      return
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('reCAPTCHA load failed'))
    document.head.appendChild(script)
  })

  return scriptPromise
}

export interface UseRecaptchaV3Return {
  executeRecaptcha: (action: string) => Promise<string | null>
}

/**
 * Mints reCAPTCHA v3 tokens on demand.
 *
 * Loads the Google reCAPTCHA script on mount (idempotent across multiple
 * callers). `executeRecaptcha(action)` resolves with a token string, or
 * `null` if the SDK couldn't be loaded / executed — caller decides how to
 * handle that (most call sites should treat it as a soft failure and let
 * the backend reject the request).
 *
 * Site key comes from NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY.
 */
export const useRecaptchaV3 = (): UseRecaptchaV3Return => {
  useEffect(() => {
    void loadScript().catch(() => undefined)
  }, [])

  const executeRecaptcha = async (action: string): Promise<string | null> => {
    if (!SITE_KEY) return null
    try {
      await loadScript()
      const grecaptcha = typeof window !== 'undefined' ? window.grecaptcha : undefined
      if (!grecaptcha) return null
      return await new Promise<string>((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(SITE_KEY, { action }).then(resolve).catch(reject)
        })
      })
    } catch {
      return null
    }
  }

  return { executeRecaptcha }
}
